<template>
  <div ref="targetDiv" class="flex flex-col min-h-screen xl:h-screen xl:overflow-hidden bg-canvas">
    <div class="flex-1 min-h-0 flex flex-col w-full">
      <ScanTable :dataResult="results" @runScan="handleRun" :scanLoader="scanLoader" @getLatestDate="getLatestDate"
        :latestDateWithAPI="getDateWithAPI" />
    </div>
    <AppFooter />
  </div>
</template>

<script>
import ScanTable from "../components/table/ScanTable.vue";
import AppFooter from "../components/Footer.vue";
import axios from "../api/axios.js";
import moment from "moment";
import { RESULT_CAP, SECURITY_TYPE_LABELS } from "../utils/data";

// Exchange MIC codes as traders know them.
const EXCHANGE_LABELS = { XNYS: "NYSE", XNAS: "NASDAQ", XASE: "NYSE American", BATS: "Cboe BZX", OOTC: "OTC" };

export default {
  name: "ScanPage",
  components: {
    ScanTable,
    AppFooter,
  },
  data() {
    return {
      results: [],
      getDateWithAPI: null,
      scanLoader: false,
      // Day the current scan ran on, to flag new 52-week highs/lows made that day.
      dateSelected: null,
      // Incremented per scan so a slower, older response never replaces a newer one.
      scanSeq: 0,
    };
  },
  methods: {
    getPercentageChange(closeLast, closeSecondLast) {
      if (!closeSecondLast) return 0;
      return ((closeLast - closeSecondLast) / closeSecondLast) * 100;
    },
    updateFormat(val) {
      return val ? moment(val).format("MM/DD/YYYY") : "N/A";
    },
    matchDate(val) {
      return !!val && val === this.dateSelected;
    },
    scrollToTop() {
      const target = this.$refs.targetDiv;
      if (target) target.scrollIntoView({ behavior: "smooth" });
    },
    mapResults(data) {
      return data.map((item) => ({
        symbol: item?.SymbolName || "N/A",
        companyName: item?.CompanyName || "N/A",
        atr: item?.ATR || 0,
        pav: item?.PAV || 0,
        typeGap: item?.TypeGap || "N/A",
        perfYtd: item?.YtdPriceReturn || 0,
        gap_percent: item?.GapPercent || 0,
        gap_vol_percent: item?.GapVolPercent || 0,
        rsi: item?._14DayRSI || 0,
        sma20: item?._20DaySMA || 0,
        sma50: item?._50DaySMA || 0,
        sma200: item?._200DaySMA || 0,
        nextEarningDate: item?.EarningDate && item.EarningDate !== "N/A" ? this.updateFormat(item.EarningDate) : "N/A",
        // "bmo" / "amc" / "dmh", or "" while the company has not announced it.
        earningHour: item?.EarningHour || "",
        avgVolume10day: item?._10DayAverageTradingVolume || 0,
        avgVolume3month: item?._3MonthAverageTradingVolume || 0,
        // Latest volume / 25-day average (the Relative Volume filter); null without an average.
        relVolume: item?.RelVolume ?? null,
        "52w_high": item?._52WeekHigh,
        "52w_low": item?._52WeekLow,
        is52W_high: this.matchDate(item?._52WeekHighDate),
        is52W_low: this.matchDate(item?._52WeekLowDate),
        close: item?.Last2Candles?.[1]?.Close || 0,
        volume: item?.Last2Candles?.[1]?.Volume || 0,
        chgPer: this.getPercentageChange(item?.Last2Candles?.[1]?.Close, item?.Last2Candles?.[0]?.Close),
        roi: item?.RoiTTM || 0,
        roa: item?.RoaTTM || 0,
        roe: item?.RoeTTM || 0,
        dividend: item?.DividendPerShareTTM || 0,
        // Next declared dividend; "N/A" (and no amount) while none is declared or the stock pays none.
        dividendExDate: item?.NextDividendExDate ? this.updateFormat(item.NextDividendExDate) : "N/A",
        dividendAmount: item?.NextDividendExDate ? item?.NextDividendAmount ?? null : null,
        dividendPayDate: item?.NextDividendPayDate ? this.updateFormat(item.NextDividendPayDate) : "N/A",
        sector: item?.Sector || "N/A",
        mktcap: item?.MarketCap || 0,
        marketCapCurrency: item?.MarketCapCurrency,
        // Industry in the classification the backend uses (Finnhub labels or GICS), never the other one: a stock not
        // classified yet shows N/A. Only an older backend, without the field, falls back to FinnhubIndustry.
        industry: ("Industry" in (item || {}) ? item.Industry : item?.FinnhubIndustry) || "N/A",
        exchange: EXCHANGE_LABELS[item?.Exchange] || item?.Exchange || "N/A",
        // Label key of the Finnhub security type ("ETP" -> "ETFs & ETPs"); other types as stored, "" when unknown.
        securityType: SECURITY_TYPE_LABELS[item?.Type] || item?.Type || "",
        jourEarnings: item?.JoursEarnings || 0,
        gapHaussMoyen: item?.GapHaussMoyenVal || 0,
        gapHaussMoyenPercent: item?.GapHaussMoyenPct || 0,
        gapBaissMoyen: item?.GapBaissMoyenVal || 0,
        gapBaissMoyenPercent: item?.GapBaissMoyenPct || 0,
        mouvHauss: item?.MouvHauss6jVal || 0,
        mouvHaussPercent: item?.MouvHauss6jPct || 0,
        mouvBaiss: item?.MouvBaiss6jVal || 0,
        mouvBaissPercent: item?.MouvBaiss6jPct || 0,
        iv: item?.IvPercentile || 0,
        ivRank: item?.IvPercentileIvRank || 0,
        ivRankPlus: item?.IvPercentileIvRankPlus || 0,
        ivPercentileTrue: item?.IvPercentileTrue || 0,
        // The Options columns: IV in percent (stored as a decimal), null without options data.
        ivPct: item?.IvPercentile > 0 ? item.IvPercentile * 100 : null,
        weeklyOptions: !!item?.WeeklyOptionable,
        oss: item?.Oss || 0,
        ossIvCheapness: item?.OssIvCheapness || 0,
        ossEarningsCycle: item?.OssEarningsCycle || 0,
        ossDteOpportunity: item?.OssDteOpportunity || 0,
        ossTermStructure: item?.OssTermStructure || 0,
        ossLiquidity: item?.OssLiquidity || 0,
        ossSkew: item?.OssSkew || 0,
        ossSkewDirection: item?.OssSkewDirection || "N/A",
        ossBestDte: item?.OssBestDte || 0,
        ossBestExpiration: item?.OssBestExpiration || "N/A",
        ossBestContract: item?.OssBestContract || "N/A",
        ossCheapVsHistory: item?.OssCheapVsHistoryPct || 0,
        ossChainDate: item?.OssChainDate || "N/A",
        // Strangle IV / HV60 (nightly OSS scan): null when not scored or not computable; the reason feeds the tooltip.
        strangleIvHv60: item?.StrangleIvHv60 ?? null,
        strangleIvHv60Reason: item?.StrangleIvHv60Reason ?? null,
        typeChandelle: item?.TypeChandelle || "N/A",
        // Top-Down scan. Percentages are already in percent; null when the stock has under a year of history.
        tdGroup: item?.TopDownGroup || "N/A",
        tdRs: item?.TopDownRs ?? null,
        tdEntry: item?.TopDownEntry || "",
        tdDistEma21: item?.TopDownDistEma21Pct ?? null,
        tdFromHigh: item?.TopDownPctFromHigh ?? null,
        tdAboveLow: item?.TopDownPctAboveLow ?? null,
        tdGrade: item?.TopDownFundGrade || "",
        tdFundScore: item?.TopDownFundScore ?? null,
        ...this.comboFields(item?.Combo),
        logo: item?.logo || "N/A",
      }));
    },
    // Option combo scans: the row's combo (per share, end-of-day quotes). Fractions become percents; every field is
    // null on the other scans.
    comboFields(c) {
      const pct = (v) => (v === null || v === undefined ? null : v * 100);
      const legs = c?.Legs || [];
      return {
        cbLegs: legs.length
          ? legs.map((l) => `${l.Action === "sell" ? "−" : "+"}${Number(l.Strike)}${l.Type === "put" ? "P" : "C"}`).join(" / ")
          : null,
        cbLegSymbols: legs.map((l) => l.OptionSymbol).join(" / ") || null,
        cbExpiration: c?.Expiration && c.Expiration !== "N/A" ? this.updateFormat(c.Expiration) : null,
        cbDte: c?.Dte ?? null,
        cbMid: c?.Mid ?? null,
        cbNatural: c?.Natural ?? null,
        cbSlippage: pct(c?.SlippagePct),
        cbMaxLoss: c?.MaxLoss ?? null,
        cbMaxGain: c?.MaxGain ?? null,
        cbCreditPct: c?.Kind === "put_credit_spread" ? pct(c.Ratio) : null,
        cbRewardRisk: c?.Kind === "call_debit_spread" ? c.Ratio ?? null : null,
        cbBreakeven: c?.BreakevenLow ?? c?.BreakevenHigh ?? null,
        cbPop: pct(c?.Pop),
        cbSigmas: c?.ShortStrikeSigmas ?? null,
        cbIvHv: c?.IvHv ?? null,
        cbMinOi: c?.MinOpenInterest ?? null,
        cbRs: c?.Rs ?? null,
        cbSessions: c?.TradingDaysToEarnings ?? null,
        cbExit: c?.ExitDate ? this.updateFormat(c.ExitDate) : null,
        cbImpliedMove: c?.ImpliedMovePct ?? null,
        // Already in percent: IV30 as a share of its level just before past reports (the scan keeps ≤ 85).
        cbIvVsPreReport: c?.IvVsPreReportPct ?? null,
        cbTheta: c?.ThetaPerDay ?? null,
      };
    },
    async handleRun(val) {
      const seq = ++this.scanSeq;
      this.scrollToTop();
      // The scan's own day, not today: past-date and weekend scans flag the
      // highs/lows made on the scanned day.
      this.dateSelected = val.date;
      this.results = [];
      this.scanLoader = true;
      try {
        const result = await axios.post(
          "/getSymbols",
          { ...val, pageNumber: 0, pageSize: RESULT_CAP },
          { headers: { Authorization: "Bearer token", "Accept-Language": this.$i18n.locale } }
        );
        if (seq !== this.scanSeq) return;
        this.results = Array.isArray(result.data) ? this.mapResults(result.data) : [];
      } catch (error) {
        if (seq !== this.scanSeq) return;
        this.$toast.error(error.response?.data?.message || this.$t("Server error occurred"));
      } finally {
        if (seq === this.scanSeq) this.scanLoader = false;
      }
    },
    async getLatestDate() {
      this.scanLoader = true;
      try {
        const latestData = await axios.get("/latestData");
        // ScanTable watches this and runs the initial scan itself, so filters
        // restored from the URL query string are honoured on first load.
        this.getDateWithAPI = latestData.data;
      } catch (error) {
        this.scanLoader = false;
        this.$toast.error(error.response?.data?.message || this.$t("Server error occurred"));
      }
    },
  },
};
</script>

<style></style>
